import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Pill } from "@/components/ui/Pill";
import type { ProductCompliance, ComplianceCertificate } from "@/lib/api/review";
import { FilePreview } from "@/components/ui/FilePreview";
import { useLocalizedNames } from "@/hooks/useLocalizedNames";

interface ProductComplianceTabProps {
  compliance: ProductCompliance | undefined;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function ProductComplianceTab({ compliance, applicationId, t }: ProductComplianceTabProps) {
  const notProvided = t("review.application.productCompliance.notProvided");
  const { resolveIndustryName, resolveCategoryName } = useLocalizedNames(
    compliance?.operatingIndustries ?? [],
    compliance?.operatingCategories ?? []
  );

  function renderCertField(label: string, cert: ComplianceCertificate | undefined | null) {
    return (
      <View className="flex-1 items-start">
        <AppText
          weight="medium"
          className="text-sm text-supporting-text uppercase tracking-wider mb-3"
        >
          {label}
        </AppText>
        {cert ? (
          <FilePreview fileKey={cert.fileUrl} isVendor={false} applicationId={applicationId} />
        ) : (
          <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
        )}
      </View>
    );
  }

  function renderCertListField(label: string, certs: ComplianceCertificate[] | undefined | null) {
    return (
      <View className="flex-1 items-start">
        <AppText
          weight="medium"
          className="text-sm text-supporting-text uppercase tracking-wider mb-3"
        >
          {label}
        </AppText>
        <View className="gap-4">
          {certs && certs.length > 0 ? (
            certs.map((cert, i) => (
              <FilePreview
                key={i}
                fileKey={cert.fileUrl}
                isVendor={false}
                applicationId={applicationId}
              />
            ))
          ) : (
            <AppText className="text-base text-dark-text-20 italic">{notProvided}</AppText>
          )}
        </View>
      </View>
    );
  }

  return (
    <View className="max-w-4xl items-start">
      <AppText weight="bold" className="text-xl text-dark-text-50 mb-2">
        {t("review.application.productCompliance.title")}
      </AppText>
      <AppText className="text-base text-dark-text-20 mb-8 leading-6">
        {t("review.application.productCompliance.description")}
      </AppText>

      <View className="h-px bg-stroke-20 mb-8 w-full" />

      {/* Operating Industries */}
      <View className="mb-8 items-start w-full">
        <AppText
          weight="medium"
          className="text-sm text-supporting-text uppercase tracking-wider mb-3"
        >
          {t("review.application.productCompliance.fields.operatingIndustries")}
        </AppText>
        <View className="flex-row flex-wrap gap-y-3 gap-x-2">
          {(compliance?.operatingIndustries ?? []).map((industry) => (
            <Pill key={industry} plainLabel={resolveIndustryName(industry)} variant="tag" />
          ))}
        </View>
      </View>

      {/* Operating Categories */}
      <View className="mb-8 items-start w-full">
        <AppText
          weight="medium"
          className="text-sm text-supporting-text uppercase tracking-wider mb-3"
        >
          {t("review.application.productCompliance.fields.operatingCategories")}
        </AppText>
        <View className="flex-row flex-wrap gap-y-3 gap-x-2">
          {(compliance?.operatingCategories ?? []).map((cat) => (
            <Pill key={cat} plainLabel={resolveCategoryName(cat)} variant="tag" />
          ))}
        </View>
      </View>

      {/* ISO 9001 + CE Marking */}
      <View className="flex-col gap-y-8 mb-8 tablet:flex-row tablet:gap-y-0 tablet:gap-x-10 w-full">
        {renderCertField(
          t("review.application.productCompliance.fields.isoCertificate"),
          compliance?.isoCertificate
        )}
        {renderCertField(
          t("review.application.productCompliance.fields.ceMarking"),
          compliance?.ceMarking
        )}
      </View>

      {/* Quality Test Reports + Eco Certifications */}
      <View className="flex-col gap-y-8 tablet:flex-row tablet:gap-y-0 tablet:gap-x-10 w-full">
        {renderCertListField(
          t("review.application.productCompliance.fields.qualityTestReports"),
          compliance?.qualityTestReports
        )}
        {renderCertListField(
          t("review.application.productCompliance.fields.ecoCertifications"),
          compliance?.ecoCertifications
        )}
      </View>
    </View>
  );
}
